import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useI18n } from '../lib/i18n'
import { IconCheck, IconLoader2, IconPhotoPlus, IconSearch, IconUpload, IconX } from '@tabler/icons-react'
import {
  listProfessionalImagesPage,
  listUploadedAssets,
  uploadAsset,
  type ProfessionalImage,
  type UploadedAsset,
} from '../lib/api'
import { useSearchPlaceholder } from '../hooks/useSearchPlaceholder'

/** Cuántas imágenes se piden de una vez; al llegar al final, otras tantas. */
const PAGE_SIZE = 48

/**
 * Selector común del dashboard: archivos propios y galería profesional.
 *
 * Con `multiple`, en vez de elegir una y cerrarse, se van marcando —cada una
 * con su número, que es el orden en que entrarán— y se añaden todas de una vez
 * con el botón del pie. Lo usa la galería de fotos de una oferta.
 */
export function ImageLibraryPickerDialog({ token, projectId, onSelect, onClose, multiple }: {
  token: string
  projectId?: string
  onSelect?: (url: string) => void
  onClose: () => void
  multiple?: { max: number; onSelectMany: (urls: string[]) => void }
}) {
  const { t } = useI18n()
  const searchPlaceholder = useSearchPlaceholder('mediaPicker.search')
  const fileRef = useRef<HTMLInputElement>(null)
  const [tab, setTab] = useState<'uploads' | 'gallery'>('uploads')
  const [uploads, setUploads] = useState<UploadedAsset[]>([])
  const [gallery, setGallery] = useState<ProfessionalImage[]>([])
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(true)
  /* Scroll infinito: lo que falta por pedir de cada pestaña y si ya se pide. */
  const [galleryCursor, setGalleryCursor] = useState<string | null>(null)
  const [uploadsMore, setUploadsMore] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)
  const [uploading, setUploading] = useState(false)
  /* La CLAVE del error, no su texto: quien lo escribe son los efectos de abajo,
     y traducir ahí obligaría a poner `t` en sus dependencias — con lo que
     cambiar de idioma volvería a pedirle las imágenes al servidor. */
  /* Lo marcado en modo varias, en el orden en que se marcó. */
  const [picked, setPicked] = useState<string[]>([])
  const togglePick = (url: string) => setPicked((rows) => (
    rows.includes(url) ? rows.filter((row) => row !== url) : rows.length >= (multiple?.max ?? 1) ? rows : [...rows, url]
  ))
  const [error, setError] = useState<'' | 'mediaPicker.uploadsFailed' | 'mediaPicker.galleryFailed' | 'mediaPicker.uploadFailed'>('')

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape' && !uploading) onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, uploading])

  useEffect(() => {
    if (tab !== 'uploads') return
    let active = true
    setLoading(true); setError('')
    void listUploadedAssets(token, PAGE_SIZE, 0)
      .then((items) => {
        if (!active) return
        setUploads(items.filter((item) => item.mimeType.startsWith('image/')))
        setUploadsMore(items.length === PAGE_SIZE)
      })
      .catch(() => { if (active) setError('mediaPicker.uploadsFailed') })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [tab, token])

  useEffect(() => {
    if (tab !== 'gallery') return
    let active = true
    setLoading(true); setError('')
    const timer = window.setTimeout(() => {
      void listProfessionalImagesPage(token, { query: query.trim(), limit: PAGE_SIZE })
        .then((page) => {
          if (!active) return
          setGallery(page.data)
          setGalleryCursor(page.meta?.nextCursor ?? null)
        })
        .catch(() => { if (active) setError('mediaPicker.galleryFailed') })
        .finally(() => { if (active) setLoading(false) })
    }, query.trim() ? 250 : 0)
    return () => { active = false; window.clearTimeout(timer) }
  }, [query, tab, token])

  /* La página siguiente de la pestaña abierta, al llegar al final. */
  const hasMore = tab === 'gallery' ? Boolean(galleryCursor) : uploadsMore
  const loadMore = async () => {
    if (loading || loadingMore || !hasMore) return
    setLoadingMore(true)
    try {
      if (tab === 'gallery') {
        const page = await listProfessionalImagesPage(token, { query: query.trim(), limit: PAGE_SIZE, cursor: galleryCursor })
        setGallery((rows) => [...rows, ...page.data.filter((item) => !rows.some((row) => row.id === item.id))])
        setGalleryCursor(page.meta?.nextCursor ?? null)
      } else {
        const page = await listUploadedAssets(token, PAGE_SIZE, uploads.length)
        setUploads((rows) => [...rows, ...page.filter((item) => item.mimeType.startsWith('image/') && !rows.some((row) => row.id === item.id))])
        setUploadsMore(page.length === PAGE_SIZE)
      }
    } catch {
      setError(tab === 'gallery' ? 'mediaPicker.galleryFailed' : 'mediaPicker.uploadsFailed')
    } finally {
      setLoadingMore(false)
    }
  }
  const loadMoreRef = useRef(loadMore)
  loadMoreRef.current = loadMore
  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel || !('IntersectionObserver' in window)) return
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) void loadMoreRef.current()
    }, { root: bodyRef.current, rootMargin: '0px 0px 240px 0px' })
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [tab, loading])

  const upload = async (files: File[]) => {
    setUploading(true); setError('')
    try {
      const urls: string[] = []
      for (const file of files) urls.push((await uploadAsset(token, file, projectId)).url)
      /* En modo varias, lo subido queda marcado y en la lista: se sigue
         eligiendo y se añade todo junto con el botón del pie. */
      if (multiple) {
        setPicked((rows) => [...rows, ...urls.filter((url) => !rows.includes(url))].slice(0, multiple.max))
        void listUploadedAssets(token).then((items) => setUploads(items.filter((item) => item.mimeType.startsWith('image/')))).catch(() => {})
        return
      }
      if (urls[0]) onSelect?.(urls[0])
      onClose()
    } catch {
      setError('mediaPicker.uploadFailed')
    } finally {
      setUploading(false)
    }
  }

  const items: { id: string; url: string; title: string; alt?: string }[] = tab === 'uploads'
    ? uploads.map((item) => ({ id: item.id, url: item.url, title: item.filename }))
    : gallery.map((item) => ({ id: item.id, url: item.url, title: item.title, alt: item.altText }))

  return createPortal(
    <div className="dash-media-picker-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !uploading) onClose() }}>
      <section className="dash-media-picker" role="dialog" aria-modal="true" aria-label={t('mediaPicker.title')}>
        <header className="dash-media-picker-head">
          <div><strong>{t('mediaPicker.title')}</strong><span>{t('mediaPicker.subtitle')}</span></div>
          <button type="button" aria-label={t('mediaPicker.close')} disabled={uploading} onClick={onClose}><IconX size={17} /></button>
        </header>

        <div className="dash-media-picker-tabs" role="tablist">
          <button type="button" role="tab" aria-selected={tab === 'uploads'} className={tab === 'uploads' ? 'is-active' : ''} onClick={() => setTab('uploads')}>{t('mediaPicker.tabUploads')}</button>
          <button type="button" role="tab" aria-selected={tab === 'gallery'} className={tab === 'gallery' ? 'is-active' : ''} onClick={() => setTab('gallery')}>{t('mediaPicker.tabGallery')}</button>
        </div>

        {tab === 'gallery' ? (
          <label className="dash-media-picker-search"><IconSearch size={15} /><input value={query} placeholder={searchPlaceholder} onChange={(event) => setQuery(event.target.value)} /></label>
        ) : null}

        <div className="dash-media-picker-body" ref={bodyRef}>
          {/* La rejilla va DENTRO de la caja que se desplaza, no en ella misma:
              siendo la misma, el navegador encogía las filas para que cupieran
              y las fotos salían aplastadas o montadas unas sobre otras. */}
          <div className="dash-media-picker-grid">
          {tab === 'uploads' ? (
            /* Solo en la caja, ocupa la caja: `is-alone` la ensancha. Con
               imágenes al lado vuelve a ser un recuadro más de la rejilla, que
               es donde tiene que estar para que se compare con ellas. */
            <button
              type="button"
              className={`dash-media-picker-upload${!loading && items.length === 0 ? ' is-alone' : ''}`}
              disabled={uploading}
              onClick={() => fileRef.current?.click()}
            >
              {uploading ? <IconLoader2 className="dash-spin" size={21} /> : <IconUpload size={21} />}
              <span><strong>{t(uploading ? 'mediaPicker.uploading' : 'mediaPicker.upload')}</strong><small>{t('mediaPicker.formats')}</small></span>
            </button>
          ) : null}
          <input ref={fileRef} hidden type="file" multiple={Boolean(multiple)} accept="image/jpeg,image/png,image/webp" onChange={(event) => { const files = Array.from(event.target.files ?? []); if (files.length) void upload(files); event.target.value = '' }} />

          {loading ? <div className="dash-media-picker-state"><IconLoader2 className="dash-spin" size={20} /><span>{t('mediaPicker.loading')}</span></div> : null}
          {!loading && items.map((item) => {
            const order = picked.indexOf(item.url)
            return (
              <button
                type="button"
                className={`dash-media-picker-item${order >= 0 ? ' is-picked' : ''}`}
                key={`${tab}-${item.id}`}
                title={item.title}
                aria-pressed={multiple ? order >= 0 : undefined}
                onClick={() => { if (multiple) togglePick(item.url); else { onSelect?.(item.url); onClose() } }}
              >
                <img src={item.url} alt={item.alt || item.title} loading="lazy" />
                <span>{item.title}</span>
                {multiple ? <b className="dash-media-picker-check" aria-hidden="true">{order >= 0 ? order + 1 : <IconCheck size={12} />}</b> : null}
              </button>
            )
          })}
          {/* El aviso de vacío, sólo en la galería.
              En «Tus imágenes» el recuadro de rayas ya ES el estado vacío: dice
              qué falta y cómo arreglarlo, y encima es lo único que hay en
              pantalla. Una frase al lado repetía eso mismo sin añadir nada —y
              colocarla en algún sitio era el problema: centrada caía en mitad de
              un vacío que ella describía, y a la altura del recuadro se leía
              como su pie. En la galería no hay nada que subir, así que ahí la
              frase es lo único que puede contestar por qué no se ve nada. */}
          {!loading && items.length === 0 && !error && tab !== 'uploads'
            ? <div className="dash-media-picker-empty"><IconPhotoPlus size={24} /><span>{t('mediaPicker.emptyGallery')}</span></div>
            : null}
          </div>
          {/* Al verse, pide más (ver `loadMore`). */}
          <div ref={sentinelRef} className="dash-media-picker-more" aria-hidden={!loadingMore}>
            {loadingMore ? <IconLoader2 className="dash-spin" size={18} /> : null}
          </div>
        </div>
        {error ? <p className="dash-media-picker-error" role="alert">{t(error)}</p> : null}
        {multiple ? (
          <footer className="dash-media-picker-foot">
            <span>{t('mediaPicker.pickedCount', { count: picked.length, max: multiple.max })}</span>
            <button type="button" className="dash-secondary-btn" disabled={uploading} onClick={onClose}>{t('mediaPicker.cancel')}</button>
            <button type="button" className="dash-primary-btn" disabled={picked.length === 0 || uploading} onClick={() => { multiple.onSelectMany(picked); onClose() }}>
              {t('mediaPicker.addPicked', { count: picked.length })}
            </button>
          </footer>
        ) : null}
      </section>
    </div>,
    document.body,
  )
}
